import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useSelectedFranchisee } from '../context/SelectedFranchiseeContext';
import { formatEmailToName } from '../utilities/formatEmailToName';
import Swal from 'sweetalert2';

function FranchiseeSelection() {
  const navigate = useNavigate();
  const { user, fetchUserData } = useUser();
  const { updateSelectedFranchisee } = useSelectedFranchisee();
  const [franchisees, setFranchisees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFranchisee, setSelectedFranchisee] = useState(null);

  useEffect(() => {
    const fetchFranchiseeAssociations = async () => {
      setFranchisees(user?.franchisees || []);
      setLoading(false);
    };

    if (user && user.id && user.franchisees && user.franchisees.length > 0) {
      fetchFranchiseeAssociations();
    }
  }, [user, navigate]);

  const handleFranchiseeSelect = (franchisee, event) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedFranchisee(franchisee);
  };

  const handleContinue = async () => {
    console.log('SelectedFranchisee:', selectedFranchisee);
    
    if (!selectedFranchisee) {
      Swal.fire({
        title: 'Please Select a Franchisee',
        text: 'You need to select a franchisee account to continue.',
        icon: 'warning'
      });
      return;
    }

    try {
      updateSelectedFranchisee(selectedFranchisee);
      console.log('Franchisee updated in context, refreshing user data...');

      // Refresh user data to ensure we have the latest information
      await fetchUserData();

      console.log('User data refreshed, navigating to home...');

      // Navigate to home page
      navigate('/');
    } catch (error) {
      console.error('Error setting franchisee:', error);
      Swal.fire({
        title: 'Error',
        text: 'Failed to set franchisee. Please try again.',
        icon: 'error'
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-hs-light-gray border-t-hs-blue mx-auto mb-6"></div>
            <div className="absolute inset-0 rounded-full h-16 w-16 border-4 border-transparent border-t-hs-blue/30 animate-spin mx-auto" style={{animationDirection: 'reverse', animationDuration: '1.5s'}}></div>
          </div>
          <p className="text-hs-background text-lg font-medium">Loading your franchisee accounts...</p>
          <div className="mt-4 flex justify-center space-x-1">
            <div className="w-2 h-2 bg-hs-blue rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
            <div className="w-2 h-2 bg-hs-blue rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
            <div className="w-2 h-2 bg-hs-blue rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
          </div>
        </div>
      </div>
    );
  }

  if (franchisees.length === 0) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
        <div className="text-center max-w-lg mx-auto">
          <div className="bg-gradient-to-br from-hs-orange/10 to-hs-orange-light/10 border border-hs-orange/20 backdrop-blur-sm rounded-2xl p-8 mb-8">
            <div className="w-16 h-16 bg-hs-orange/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-hs-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold mb-3 text-hs-background">No Franchisee Accounts Found</h2>
            <p className="text-hs-gray leading-relaxed">
              You don't have any franchisee accounts assigned. Please contact your administrator to get access to franchisee accounts.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/magic-link-request')}
            className="bg-gradient-to-r from-hs-blue to-hs-dark-blue hover:from-hs-dark-blue hover:to-hs-blue text-hs-background font-semibold py-3 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 overflow-y-auto cursor-pointer">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(0,145,174,0.1),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(66,91,118,0.1),transparent_50%)]"></div>
      </div>
      
      {/* Main Content */}
      <div className="relative z-10 min-h-full flex flex-col">
        {/* Header Section */}
        <div className="flex-shrink-0 pt-8 pb-4 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-hs-blue to-hs-dark-blue rounded-2xl mb-6 shadow-2xl">
              <svg className="w-8 h-8 text-hs-background" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-hs-background mb-4 bg-gradient-to-r from-hs-background to-hs-gray bg-clip-text text-transparent">
              Welcome back, {formatEmailToName(user.email)}!
            </h1>
            <p className="text-lg text-hs-gray max-w-2xl mx-auto leading-relaxed">
              You have access to multiple franchisee accounts. Select which account you'd like to use for this session.
            </p>
          </div>
        </div>

        {/* Franchisee Cards Section */}
        <div className="flex-1 px-4 sm:px-6 lg:px-8 py-4">
          <div className="w-full max-w-7xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 justify-items-center">
              {franchisees.map((franchisee) => (
                <div
                  key={franchisee.id}
                  onClick={(event) => handleFranchiseeSelect(franchisee, event)}
                  className={`group cursor-pointer relative overflow-hidden rounded-xl transition-all duration-500 transform hover:scale-[1.02] w-full max-w-xs ${
                    selectedFranchisee?.id === franchisee.id
                      ? 'ring-2 ring-hs-blue ring-offset-2 ring-offset-gray-900 shadow-2xl shadow-hs-blue/25'
                      : 'hover:shadow-xl hover:shadow-hs-blue/10'
                  }`}
                >
                  {/* Card Background */}
                  <div className={`absolute inset-0 transition-all duration-500 ${
                    selectedFranchisee?.id === franchisee.id
                      ? 'bg-gradient-to-br from-hs-background to-gray-50'
                      : 'bg-gradient-to-br from-gray-800 to-gray-700 group-hover:from-gray-700 group-hover:to-gray-600'
                  }`}>
                    <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-black/10"></div>
                  </div>
                  
                  {/* Card Content */}
                  <div className="relative z-10 p-4">
                    {/* Avatar */}
                    <div className="flex items-center justify-between mb-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shadow-lg transition-all duration-300 ${
                        selectedFranchisee?.id === franchisee.id
                          ? 'bg-gradient-to-br from-hs-blue to-hs-dark-blue text-hs-background'
                          : 'bg-gradient-to-br from-hs-light-gray to-hs-dark-gray text-hs-background group-hover:from-hs-blue group-hover:to-hs-dark-blue'
                      }`}>
                        <span className="text-sm font-bold">
                          {franchisee.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      
                      {/* Selection Indicator */}
                      {selectedFranchisee?.id === franchisee.id && (
                        <div className="flex items-center justify-center w-5 h-5 bg-hs-blue rounded-full shadow-lg">
                          <svg className="w-3 h-3 text-hs-background" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                    
                    {/* Franchisee Info */}
                    <div className="mb-3">
                      <h3 className={`text-base font-bold mb-1 transition-colors duration-300 ${
                        selectedFranchisee?.id === franchisee.id ? 'text-gray-900' : 'text-hs-background'
                      }`}>
                        {franchisee.name}
                      </h3>
                      <p className={`text-xs font-medium transition-colors duration-300 ${
                        selectedFranchisee?.id === franchisee.id ? 'text-gray-600' : 'text-hs-gray'
                      }`}>
                        Owner: {franchisee.owner}
                      </p>
                    </div>

                    {/* Technical Details */}
                    <div className={`p-2 rounded-lg transition-all duration-300 ${
                      selectedFranchisee?.id === franchisee.id 
                        ? 'bg-gray-100' 
                        : 'bg-gray-800/50 group-hover:bg-gray-700/50'
                    }`}>
                      <div className="flex items-center justify-between">
                        <span className={`text-xs font-semibold uppercase tracking-wide ${
                          selectedFranchisee?.id === franchisee.id ? 'text-gray-600' : 'text-hs-gray'
                        }`}>
                          HubSpot ID
                        </span>
                        <span className={`text-hs-blue text-xs px-2 py-1 rounded font-medium ${
                          selectedFranchisee?.id === franchisee.id 
                            ? 'bg-gray-200 text-gray-800' 
                            : 'bg-gray-700 text-hs-background'
                        }`}>
                          {franchisee.UserFranchiseeAssociation?.hubspotID || 'Not set'}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Hover Effect Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-hs-blue/0 to-hs-blue/0 group-hover:from-hs-blue/5 group-hover:to-hs-blue/10 transition-all duration-500 rounded-xl"></div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Action Section */}
        <div className="flex-shrink-0 px-4 sm:px-6 lg:px-8 pb-6 pt-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-gray-800/50 to-gray-700/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50">
              <div className="text-center">
                <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                  <button
                    type="button"
                    onClick={handleContinue}
                    disabled={!selectedFranchisee}
                    className={`px-8 py-3 rounded-xl font-semibold text-base transition-all duration-300 transform hover:scale-105 shadow-lg ${
                      selectedFranchisee
                        ? 'bg-gradient-to-r from-hs-blue to-hs-dark-blue hover:from-hs-dark-blue hover:to-hs-blue text-hs-background shadow-xl hover:shadow-2xl hover:shadow-hs-blue/25'
                        : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                      <span>Continue with Selected Account</span>
                    </div>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => navigate('/magic-link-request')}
                    className="px-6 py-3 rounded-xl font-semibold text-hs-gray hover:text-hs-background hover:bg-gray-600 transition-all duration-300 border border-gray-600 hover:border-gray-500 backdrop-blur-sm"
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                      </svg>
                      <span>Back to Login</span>
                    </div>
                  </button>
                </div>
                
                {/* Help Text */}
                <div className="mt-4 pt-4 border-t border-gray-700/50">
                  <p className="text-hs-gray text-xs flex items-center justify-center space-x-2">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>You can switch between franchisee accounts by logging out and logging back in.</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FranchiseeSelection;

